/**
 * ZTools - Zod schemas for the marketplace/browse surface of the Tools API
 * (/api/tools/*).
 *
 * Controller: tools/tools.controller.ts
 *
 * Covers the read-only marketplace surface consumed by the workflow plugin
 * pickers: paginated marketplace listing, Typesense search/browse, the user's
 * private tools, and single-tool lookups (by id and by Pipedream private name).
 * The credential/OAuth/document routes on the same controller are not part of
 * this slice and stay on the legacy path.
 *
 * Response payloads are wrapped in `z.custom<T>()` rather than validated field
 * by field: `GenericTool` carries `Date | string` timestamps that must survive
 * on the wire untouched, and the Typesense `ToolSearchResult` highlight shape is
 * provider-driven — the legacy hooks did zero validation, so a strict schema
 * here would be a behaviour change (a surprise payload would throw instead of
 * render). The closed `Params`/`PathVars` schemas are the real contract.
 */

import { z } from "zod";

import type { ActionType, DataSchemaType, DbEntity } from "../../../client";
import type { PaginationMeta } from "../../../shared/core/base.types";
import type { GenericTool } from "../../../shared/domain/tools/tool-catalog";
import { ApiSuccessSchema } from "../../common/response.schema";
import { ConfirmOauthBodySchema, ConfirmOauthResponseDataSchema } from "./confirm-oauth.schemas";

/**
 * The `config` field of a runExternalToolTest body — a single tool action
 * (ActionType) plus the test-time extras the frontend attaches: the credential
 * to run under (Pipedream), any manual parametersSetup, and display metadata.
 *
 * 🔴 `accessCardId` IS DECLARED BECAUSE THE BACKEND ALREADY READS IT AND THIS
 * TYPE WAS THE ONLY THING STOPPING THE DASHBOARD SENDING IT.
 * `ToolsExecutionService.runExternalToolTest` threads `config.accessCardId` into
 * Access Card enforcement, and the two `public/v1` test routes reach it because
 * they forward a STORED skill config, which carries the field. The dashboard's
 * own PLUGIN skill screen holds the chosen card in state one tab away from its
 * Test button and could not put it on the wire — so a skill scoped to a
 * read-only card was TESTED under the credential's master card and RUN by the
 * agent under the named one. A test that passes under a broader card predicts
 * nothing about the run.
 *
 * ⚠️ The body slot is `z.custom<RunExternalToolTestConfig>()`, which validates
 * NOTHING and strips NOTHING — measured against the installed zod 4.1.13, with a
 * plain `z.object` as the control that does strip. So the wire has always been
 * able to carry this field; the gap was type-level only, and no schema change is
 * needed to close it.
 */
export interface RunExternalToolTestConfig {
  config: ActionType & {
    toolCredentialsId?: string;
    parametersSetup?: unknown;
    /**
     * The named Access Card the skill runs under. OPTIONAL and passed through
     * verbatim: absent must stay absent, because the backend resolves the
     * credential's MASTER card for an unnamed call and substituting any default
     * here would silently scope every unconfigured test.
     */
    accessCardId?: string;
  };
  name: string;
  description?: string;
  parameters?: unknown[];
}

/**
 * A tool credential as returned by getCredentialsOfTool — the exact masked
 * projection the backend selects (id/type/status/name/accountIdentifier/
 * createdAt + the joined nodeCredentials row and creator summary). Field types
 * are derived from DbEntity so they track the schema; no secret/token columns
 * are selected server-side. Narrower than the legacy hook's full-DbEntity type,
 * but consumers only read these fields.
 */
export type ToolCredentialListItem = Pick<
  DbEntity.ToolCredentials,
  "id" | "type" | "status" | "name" | "accountIdentifier" | "createdAt"
> & {
  nodeCredentials: DbEntity.NodeCredentials | null;
  creator: Pick<DbEntity.User, "id" | "firstName" | "lastName"> | null;
};

/** Projection returned by the rename endpoint (never read by consumers). */
export interface RenamedToolCredential {
  id: string;
  name: string | null;
}

/**
 * A single Typesense (or SQL-fallback) search hit. Mirrors `ToolSearchResult`
 * in tools/services/typesense-tools.service.ts. Self-contained to this slice —
 * no consumer imports it directly, they read it off the response.
 */
export interface ToolSearchResult {
  id: string;
  name: string;
  description: string;
  type: string;
  imageUrl?: string;
  category?: string;
  /** Array of category strings from Pipedream. */
  categories?: string[];
  /** Where the match was found: 'name' = priority 1, 'description' = priority 2. */
  matchType: "name" | "description";
  highlight?: {
    name?: string;
    description?: string;
  };
}

/** Paginated marketplace listing (GenericTool + pagination metadata). */
export interface MarketplaceToolsResponse {
  data: GenericTool[];
  meta: PaginationMeta;
}

/** Typesense search response: hits, category facet counts, and total. */
export interface SearchToolsResponse {
  tools: ToolSearchResult[];
  facets: { category: string; count: number }[];
  total: number;
}

/** Browse response — same shape as search (hits, facets, total). */
export interface BrowseToolsResponse {
  tools: ToolSearchResult[];
  facets: { category: string; count: number }[];
  total: number;
}

/** The organization's private (non-public) tools. */
export interface MyToolsResponse {
  tools: ToolSearchResult[];
  total: number;
}

const GetMarketplaceToolsParamsSchema = z.object({
  page: z.number(),
  perPage: z.number(),
  search: z.string(),
  includePipedream: z.boolean().optional(),
  onlyPipedream: z.boolean().optional()
});

const SearchToolsParamsSchema = z.object({
  q: z.string(),
  category: z.string().optional(),
  uncategorized: z.boolean().optional(),
  /**
   * Keep only tools that can START a workflow — those carrying at least one
   * Pipedream source component. The plugin TRIGGER picker sets this; the
   * "Add an action" picker does not, because an action-only tool is a perfectly
   * good action.
   */
  onlyWithTriggers: z.boolean().optional(),
  limit: z.number().optional(),
  offset: z.number().optional()
});

const BrowseToolsParamsSchema = z.object({
  category: z.string().optional(),
  uncategorized: z.boolean().optional(),
  sort: z.enum(["newest", "name", "popular"]).optional(),
  /** See SearchToolsParamsSchema.onlyWithTriggers. */
  onlyWithTriggers: z.boolean().optional(),
  limit: z.number().optional(),
  offset: z.number().optional()
});

const GetMyToolsParamsSchema = z.object({
  limit: z.number().optional(),
  offset: z.number().optional()
});

const GetMarketplaceToolPathVarsSchema = z.object({ toolId: z.string() });

const GetMarketplaceToolWithPrivateNamePathVarsSchema = z.object({
  privateName: z.string()
});

const GetCredentialsOfToolPathVarsSchema = z.object({ toolId: z.string() });

const ToolCredentialIdPathVarsSchema = z.object({ toolCredentialsId: z.string() });

const RenameToolCredentialBodySchema = z.object({ name: z.string() });

const RunExternalToolTestPathVarsSchema = z.object({ toolId: z.string() });

/**
 * Outer test-run body. The ActionType-heavy `config` is passed through with
 * z.custom (its shape is a large domain union, not worth re-deriving in Zod);
 * the outer structure is validated. `parameters` is optional to match the
 * frontend call sites (the Swagger doc marks it required, which is a lie).
 */
const RunExternalToolTestBodySchema = z.object({
  config: z.custom<RunExternalToolTestConfig>(),
  input: z.record(z.string(), z.unknown()),
  parameters: z.array(z.object({ name: z.string(), value: z.unknown() })).optional()
});

const ToolIdPathVarsSchema = z.object({ toolId: z.string() });

/** createCredentialForUserHttp body — the raw API key the user pastes in. */
const CreateCredentialForUserHttpBodySchema = z.object({ apiKey: z.string() });

const GenerateTestParametersBodySchema = z.object({
  actionName: z.string(),
  prompt: z.string(),
  schema: z.custom<Record<string, DataSchemaType>>()
});

export const ZTools = {
  /** GET /api/tools/marketplace — paginated marketplace listing with filters. */
  GetMarketplaceTools: {
    method: "GET" as const,
    path: "/api/tools/marketplace",
    Params: GetMarketplaceToolsParamsSchema,
    Response: ApiSuccessSchema(z.custom<MarketplaceToolsResponse>())
  },

  /** GET /api/tools/search — Typesense search with typo tolerance + facets. */
  SearchTools: {
    method: "GET" as const,
    path: "/api/tools/search",
    Params: SearchToolsParamsSchema,
    Response: ApiSuccessSchema(z.custom<SearchToolsResponse>())
  },

  /** GET /api/tools/browse — browse all tools with category filtering. */
  BrowseTools: {
    method: "GET" as const,
    path: "/api/tools/browse",
    Params: BrowseToolsParamsSchema,
    Response: ApiSuccessSchema(z.custom<BrowseToolsResponse>())
  },

  /** GET /api/tools/my-tools — the organization's private tools. */
  GetMyTools: {
    method: "GET" as const,
    path: "/api/tools/my-tools",
    Params: GetMyToolsParamsSchema,
    Response: ApiSuccessSchema(z.custom<MyToolsResponse>())
  },

  /** GET /api/tools/marketplace/:toolId — single marketplace tool by id. */
  GetMarketplaceTool: {
    method: "GET" as const,
    path: "/api/tools/marketplace/:toolId",
    PathVars: GetMarketplaceToolPathVarsSchema,
    Response: ApiSuccessSchema(z.custom<GenericTool>())
  },

  /** GET /api/tools/pipedreamMarketplace/:privateName — Pipedream tool by private name. */
  GetMarketplaceToolWithPrivateName: {
    method: "GET" as const,
    path: "/api/tools/pipedreamMarketplace/:privateName",
    PathVars: GetMarketplaceToolWithPrivateNamePathVarsSchema,
    Response: ApiSuccessSchema(z.custom<GenericTool>())
  },

  /** GET /api/tools/getCredentialsOfTool/:toolId — credentials configured for a tool. */
  GetCredentialsOfTool: {
    method: "GET" as const,
    path: "/api/tools/getCredentialsOfTool/:toolId",
    PathVars: GetCredentialsOfToolPathVarsSchema,
    Response: ApiSuccessSchema(z.custom<ToolCredentialListItem[]>())
  },

  /** PATCH /api/tools/renameToolCredential/:toolCredentialsId — rename a credential. */
  RenameToolCredential: {
    method: "PATCH" as const,
    path: "/api/tools/renameToolCredential/:toolCredentialsId",
    PathVars: ToolCredentialIdPathVarsSchema,
    Body: RenameToolCredentialBodySchema,
    Response: ApiSuccessSchema(z.custom<RenamedToolCredential>())
  },

  /** DELETE /api/tools/deleteToolCredentials/:toolCredentialsId — delete a credential. */
  DeleteToolCredential: {
    method: "DELETE" as const,
    path: "/api/tools/deleteToolCredentials/:toolCredentialsId",
    PathVars: ToolCredentialIdPathVarsSchema,
    Response: ApiSuccessSchema(z.null())
  },

  /** POST /api/tools/runExternalToolTest/:toolId — execute a tool action against test input. */
  RunExternalToolTest: {
    method: "POST" as const,
    path: "/api/tools/runExternalToolTest/:toolId",
    PathVars: RunExternalToolTestPathVarsSchema,
    Body: RunExternalToolTestBodySchema,
    // Freeform tool-execution result — no fixed shape.
    Response: ApiSuccessSchema(z.unknown())
  },

  /** POST /api/tools/generateTestParameters — AI-generate test params for an action. */
  GenerateTestParameters: {
    method: "POST" as const,
    path: "/api/tools/generateTestParameters",
    Body: GenerateTestParametersBodySchema,
    Response: ApiSuccessSchema(z.custom<Record<string, unknown>>())
  },

  /**
   * POST /api/tools/getOauthRedirection/:toolId — begin an OAuth connect flow.
   * Creates a PENDING credential and returns the provider authorization URL the
   * frontend redirects the user to. No request body; the optional `name` query
   * param is never sent by any frontend caller so it is not part of the contract.
   */
  GetOauthRedirection: {
    method: "POST" as const,
    path: "/api/tools/getOauthRedirection/:toolId",
    PathVars: ToolIdPathVarsSchema,
    Response: ApiSuccessSchema(z.object({ authUrl: z.string() }))
  },

  /**
   * POST /api/tools/confirmOauth/:toolId — complete the OAuth flow whose
   * handshake id is `state`: consume it once, exchange the code, and write the
   * ONE pending credential it names. Returns `{ success: true }`; no secret is
   * echoed back. Every refusal is a 4xx carrying one of
   * `EXTERNAL_TOOL_OAUTH_ERROR_CODES`.
   */
  ConfirmOauth: {
    method: "POST" as const,
    path: "/api/tools/confirmOauth/:toolId",
    PathVars: ToolIdPathVarsSchema,
    Body: ConfirmOauthBodySchema,
    Response: ApiSuccessSchema(ConfirmOauthResponseDataSchema)
  },

  /**
   * POST /api/tools/createCredentialForUserHttp/:toolId — store an API-key
   * credential for a user_http tool. Returns the masked credential projection
   * (id/type/status/name/accountIdentifier/createdAt/creator) — the `apikey`
   * column is never selected, so the key is not echoed back despite the legacy
   * hook's `{ apiKey }` return type (which was a fiction consumers cast away).
   */
  CreateCredentialForUserHttp: {
    method: "POST" as const,
    path: "/api/tools/createCredentialForUserHttp/:toolId",
    PathVars: ToolIdPathVarsSchema,
    Body: CreateCredentialForUserHttpBodySchema,
    Response: ApiSuccessSchema(z.custom<ToolCredentialListItem>())
  }
} as const;
